import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SearchForm from "@/components/SearchForm";
import ParkingSpotCard from "@/components/ParkingSpotCard";
import Map from "@/components/Map";
import { Button } from "@/components/ui/button";
import { ArrowRight, Banknote, CalendarCheck, CheckCircle2, LockKeyhole, MapPin, Shield, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { expandAvailability } from "@/lib/availability";
import { addDays } from "date-fns";
import { SEOHead } from "@/components/SEOHead";
import { useIsMobile } from "@/hooks/use-mobile";
import heroImage from "@/assets/sharepark-hero.jpg";

import listingPlaceholder from "@/assets/listing-placeholder.svg";

// Fallback-billede når en plads mangler fotos
const placeholderUrl = listingPlaceholder;

const Index = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Hent synlige og godkendte listings fra Supabase
  const { data, isLoading, error } = useQuery({
    queryKey: ["listings", "visible"],
    queryFn: async () => {
      const { data: listings, error } = await supabase
        .from("listings")
        .select("id,title,address,base_price_day,size,photos,facilities,geo_lat,geo_lng")
        .eq("visibility", true)
        .eq("approval_status", "approved")
        .order("created_at", { ascending: false });
      if (error) throw error;
      if (!listings?.length) return [];

      // Hent availability for alle listings
      const listingIds = listings.map(l => l.id);
      const { data: availabilityData } = await supabase
        .from("availability")
        .select("*")
        .in("listing_id", listingIds);

      // Filtrer listings med fremtidig availability
      const now = new Date();
      const futureEnd = addDays(now, 365); // Tjek op til et år frem

      const listingsWithAvailability = listings.filter(listing => {
        const avRows = availabilityData?.filter(av => av.listing_id === listing.id) ?? [];
        if (avRows.length === 0) return false; // Skjul hvis ingen availability rows
        
        const windows = expandAvailability(avRows as any, now, futureEnd);
        return windows.length > 0; // Vis kun hvis der er fremtidige vinduer
      });

      return listingsWithAvailability;
    },
  });

  async function handleBecomeHost() {
    const { data: auth } = await supabase.auth.getUser();
    if (auth.user) {
      navigate("/create-listing");
    } else {
      navigate("/auth?mode=signin&redirect=/create-listing");
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Privat parkering i Danmark | SharePark"
        description="Find og book private parkeringspladser i Danmark. Se ledige pladser, priser og udlej din egen parkering sikkert med SharePark."
        keywords="privat parkering, lej parkeringsplads, udlej parkeringsplads, ledig parkering, parkering danmark, billig parkering, SharePark"
        url="https://sharepark.dk/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "SharePark",
            url: "https://sharepark.dk/",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://sharepark.dk/search?query={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Hvordan booker jeg en privat parkeringsplads?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Søg efter en ledig parkeringsplads, vælg datoer, gennemgå prisen og gennemfør betalingen. Når betalingen er verificeret, oprettes bookingen i SharePark.",
                },
              },
              {
                "@type": "Question",
                name: "Kan jeg afbestille en booking?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Ja, afbestilling håndteres efter pladsens afbestillingsvilkår. Refusion afhænger af hvor tæt på starttidspunktet bookingen afbestilles.",
                },
              },
              {
                "@type": "Question",
                name: "Hvornår får jeg den fulde adresse?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Husnummer og fuld adresse skjules i søgning og på kortet, indtil bookingen er bekræftet. Derefter kan lejeren se de nødvendige adresseoplysninger.",
                },
              },
            ],
          },
        ]}
      />
      <Header />

      <section className="relative min-h-[680px] overflow-hidden bg-foreground">
        <img
          src={heroImage}
          alt="Privat parkeringsplads ved danske boliger"
          width={1600}
          height={960}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/75 via-foreground/35 to-background" />
        <div className="container relative mx-auto flex min-h-[680px] flex-col justify-center px-4 pb-24 pt-20">
          <div className="max-w-4xl space-y-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/25 bg-background/15 px-4 py-2 text-sm font-medium text-primary-foreground backdrop-blur-md">
              <Sparkles className="h-4 w-4" />
              Privat parkering gjort enkel
            </div>
            <div className="space-y-5">
              <h1 className="max-w-3xl text-4xl font-bold leading-[1.05] text-primary-foreground md:text-6xl lg:text-7xl">
                Parkér hos private. Udlej når pladsen står tom.
              </h1>
              <p className="max-w-2xl text-lg leading-8 text-primary-foreground/90 md:text-xl">
                Find en konkret plads tæt på din destination — eller tjen penge på din egen indkørsel, gårdplads eller p-plads.
              </p>
            </div>
            <SearchForm />
            <div className="grid gap-3 text-sm font-medium text-primary-foreground/95 sm:grid-cols-3">
              <div className="flex items-center gap-2 rounded-full bg-background/15 px-4 py-3 backdrop-blur-md">
                <Shield className="h-4 w-4 text-success" />
                Betaling via Stripe
              </div>
              <div className="flex items-center gap-2 rounded-full bg-background/15 px-4 py-3 backdrop-blur-md">
                <CalendarCheck className="h-4 w-4 text-warning" />
                Book ledige datoer
              </div>
              <div className="flex items-center gap-2 rounded-full bg-background/15 px-4 py-3 backdrop-blur-md">
                <MapPin className="h-4 w-4 text-accent" />
                Adresse deles efter booking
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b bg-background py-14">
        <div className="container mx-auto px-4">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-bold text-foreground">Tryg booking fra søgning til parkering</h2>
            <p className="mt-3 text-muted-foreground">
              SharePark er bygget til private parkeringspladser: tydelige datoer, sikker betaling og adressebeskyttelse før booking.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { icon: MapPin, title: "Find en konkret plads", text: "Søg på område, vælg datoer og se pris, billeder, størrelse og faciliteter før du booker." },
              { icon: LockKeyhole, title: "Adresse beskyttes", text: "Husnummer skjules i søgning og på kort, indtil bookingen er bekræftet." },
              { icon: Shield, title: "Betaling og kvittering", text: "Betaling kører via Stripe, og bookingen oprettes først når betalingen er verificeret." },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="rounded-lg border bg-card p-5 shadow-sm">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Map Section - Only show on desktop */}
      {!isMobile && (
        <section className="border-y bg-muted/30 py-16">
          <div className="container mx-auto px-4">
            <div className="mb-8 flex items-end justify-between gap-4">
              <div>
              <h2 className="text-3xl font-bold text-foreground mb-2">
                Se parkeringspladser på kort
              </h2>
              <p className="text-muted-foreground">
                Sammenlign områder, afstande og priser visuelt.
              </p>
              </div>
              <Button variant="outline" onClick={() => navigate("/search")}>Åbn søgning</Button>
            </div>
            {!isLoading && data && (
              <Map 
                listings={data.map((l: any) => ({
                  id: l.id,
                  title: l.title,
                  address: l.address,
                  geo_lat: l.geo_lat,
                  geo_lng: l.geo_lng,
                  base_price_day: l.base_price_day,
                }))} 
              />
            )}
          </div>
        </section>
      )}

      {/* Featured Parking Spots */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-2">
                Ledige pladser lige nu
              </h2>
              <p className="text-muted-foreground">
                Kvalitetspladser med billeder, pris og fremtidig tilgængelighed.
              </p>
            </div>
            
            <Button variant="outline" onClick={() => navigate("/search")}>Se alle <ArrowRight className="h-4 w-4" /></Button>

          </div>

          {!isLoading && !error && data && data.length === 0 ? (
            <div className="rounded-xl border border-dashed bg-card p-10 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Vær den første til at liste din plads</h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                Der er endnu ingen ledige pladser. Opret din egen og bliv synlig for de første lejere.
              </p>
              <Button className="mt-5" onClick={handleBecomeHost}>
                Opret din plads <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading && <div>Henter…</div>}
              {error && <div className="text-red-600">Kunne ikke hente pladser</div>}
              {data?.map((l: any) => (
                <ParkingSpotCard
                  key={l.id}
                  id={l.id}
                  title={l.title}
                  address={l.address}
                  price={(l.base_price_day ?? 0) / 100}
                  size={l.size}
                  hasCharger={Boolean(l?.facilities?.charger)}
                  isCovered={Boolean(l?.facilities?.covered)}
                  photos={
                    Array.isArray(l.photos) && l.photos.length
                      ? l.photos
                      : [placeholderUrl]
                  }
                  distance=""
                />
              ))}
            </div>
          )}

        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-16">
        <div className="container mx-auto grid gap-8 px-4 md:grid-cols-[1fr_auto] md:items-center">
          <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-3 py-1 text-sm font-medium text-primary-foreground">
            <Banknote className="h-4 w-4" /> 85% går til værten
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
            Har du en ledig parkeringsplads?
          </h2>
          <p className="max-w-2xl text-lg text-primary-foreground/90">
            Tjen penge på din ubrugte parkeringsplads. Det er nemt at komme i gang og helt gratis at oprette.
          </p>
          </div>
          <Button
            size="lg"
            variant="secondary"
            onClick={handleBecomeHost}
          >
            Bliv udlejer <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 p-3 shadow-lg backdrop-blur md:hidden">
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={() => navigate("/search")} className="w-full">
            Find plads
          </Button>
          <Button variant="outline" onClick={handleBecomeHost} className="w-full">
            Udlej plads
          </Button>
        </div>
        <div className="mt-2 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-success" />
          Sikker betaling · Adresse efter booking
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Index;
